import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormField,
  FormFieldLayout,
  FormReadonlyField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import { Dialog } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Spinner } from '@/components/ui/spinner'
import {
  fetchInstrumentsAction,
  fetchPlatformTypesAction,
  fetchTradingAccountInstrumentTypeAction,
} from '@/store/trading-accounts/trading-accounts.actions'
import { useApiClient } from '@/utils/api/ApiClient'
import { TradingAccountsApiClient } from '@/utils/api/tradingAccountsApi'
import { isEmpty } from '@/utils/is-empty'
import { GridSortChangeEvent } from '@progress/kendo-react-grid'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { AccountGroupDto } from 'api/Trading-Accounts/Types/AccountGroupDto'
import {
  IntroducingBrokerDetailedProfileDto,
  StandardCommission,
  StandardCommissionDto,
} from 'api/Trading-Accounts/Types/IntroducingBrokerDetailedProfileDto'
import { IntroducingBrokerProfileAccountType } from 'api/Trading-Accounts/Types/IntroducingBrokerProfileAccountType'
import { UpdateIntroducingBrokerProfile } from 'api/Trading-Accounts/Types/UpdateIntroducingBrokerProfile'
import AddButton from 'components/AddButton/AddButton'
import { Select } from 'components/Select'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { ChevronDown } from 'lucide-react'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchRebateCalcMethodTypesAction } from 'store/partnerships/partnerships.actions'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { FormSubmitValues } from 'utils/formValidation'
import { hasPermission } from 'utils/permissionsValidator'
import { TableStateContextData } from 'utils/TableStateContext'

import { CommissionModal } from '../ComissionModal'
import { StandardCommissionsGrid } from './StandardCommissionsGrid'

const IntroducingBrokerProfileFormUI: React.FC<
  FormikProps<IntroducingBrokerDetailedProfileDto> & OuterProps
> = (props) => {
  const {
    values,
    resetForm,
    submitForm,
    setFieldValue,
    onDownload,
    onUploadModalOpen,
    isSubmitting,
    isValid,
  } = props

  const [isEdit, setEdit] = useState<boolean>(false)
  const [commissionModal, setCommissionModal] = useState<{
    data?: StandardCommission
    visible: boolean
  }>({
    visible: false,
  })

  const dispatch = useAppDispatch()
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const instrumentTypes = useAppSelector(
    (state) => state.tradingAccounts.tradingAccountInstrumentType
  )
  const platformTypes = useAppSelector((state) => state.tradingAccounts.platformTypes)
  const rebateCalcMethods = useAppSelector((state) => state.partnerships.rebateCalcMethods)
  const instruments = useAppSelector((state) => state.tradingAccounts.instruments)

  useEffect(() => {
    if (isEmpty(instrumentTypes))
      dispatch(fetchTradingAccountInstrumentTypeAction(tradingAccountsApiClient))
    if (isEmpty(platformTypes)) dispatch(fetchPlatformTypesAction(tradingAccountsApiClient))
    if (isEmpty(rebateCalcMethods))
      dispatch(fetchRebateCalcMethodTypesAction(partnershipsApiClient))
    if (isEmpty(instruments)) {
      dispatch(fetchInstrumentsAction(tradingAccountsApiClient))
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const companyOptions = useMemo(
    () => [
      { key: 0, value: 0, label: 'None', text: 'None' },
      ...props.tickmillCompanies.map((company) => ({
        key: company.id,
        value: company.id,
        label: company.name,
        text: company.name,
      })),
    ],
    [props.tickmillCompanies]
  )
  const selectedCompanyId = Number(values.tickmillCompany?.id ?? 0)
  const selectedCompanyName =
    companyOptions.find((companyOption) => Number(companyOption.value) === selectedCompanyId)
      ?.label ?? 'None'

  const handleCancel = () => {
    setEdit(false)
    resetForm()
  }

  const handleSubmitForm = async () => {
    setEdit(false)
    await submitForm()
  }

  const handleAddStandardCommissions = () => {
    setCommissionModal({ visible: true, data: undefined })
  }

  const handleEditStandardCommissions = (data: StandardCommission) => {
    setCommissionModal({ visible: true, data })
  }

  const handleChangeCommission = (data: StandardCommission) => {
    let standardCommissions: StandardCommission[] = []

    if (commissionModal.data) {
      standardCommissions = [
        ...values.standardCommissions.filter((x) => x !== commissionModal.data),
        data,
      ]
    } else {
      standardCommissions = [...values.standardCommissions, data]
    }
    setFieldValue('standardCommissions', standardCommissions)
    setCommissionModal({ visible: false, data: undefined })

    updateStandardCommissions(standardCommissions)
  }

  const handleDeleteStandardCommission = (data: StandardCommission) => {
    const standardCommissions = values.standardCommissions.filter((x) => x !== data)

    setFieldValue('standardCommissions', standardCommissions)

    updateStandardCommissions(standardCommissions)
  }

  const updateStandardCommissions = (standardCommissions: StandardCommission[]) => {
    partnershipsApiClient.updateIntroducingBrokerProfileStandardCommissions(values.id, {
      StandardCommissions: standardCommissions as StandardCommissionDto[],
    })
  }

  return (
    <>
      {commissionModal.visible && (
        <Dialog
          open={commissionModal.visible}
          onOpenChange={(open) => !open && setCommissionModal({ visible: false, data: undefined })}
        >
          <CommissionModal
            instruments={instruments}
            instrumentTypes={instrumentTypes}
            platformTypes={platformTypes}
            rebateCalcMethods={rebateCalcMethods}
            handleChange={handleChangeCommission}
            introducingBrokerProfileAccountType={props.introducingBrokerProfileAccountType}
            data={commissionModal.data}
          />
        </Dialog>
      )}
      <Form>
        <DataGrid
          rightAddon={
            <ButtonGroup>
              {!isEdit ? (
                <Button
                  type='button'
                  disabled={
                    !hasPermission(
                      props.permissions,
                      PermissionsEnum.CanEditIntroducingBrokerProfile
                    )
                  }
                  onClick={() => setEdit(true)}
                  data-test='edit-button'
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={handleCancel}
                    data-test='cancel-button'
                  >
                    Cancel
                  </Button>
                  <Button
                    key='button'
                    type='button'
                    onClick={handleSubmitForm}
                    disabled={!isValid || isSubmitting}
                    data-test='save-button'
                  >
                    Save
                    {isSubmitting && <Spinner data-icon='inline-start' />}
                  </Button>
                </>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      type='button'
                      variant='outline'
                      size='icon'
                      aria-label='More options'
                      data-test='more-button'
                    />
                  }
                >
                  <ChevronDown />
                </DropdownMenuTrigger>
                <DropdownMenuContent className='min-w-max' align='end'>
                  <DropdownMenuItem
                    onSelect={() => props.setAuditLogModal(true)}
                    data-test='audit-log-menu-button'
                  >
                    Audit Log
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={props.handleClone} data-test='clone-menu-button'>
                    Clone
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </ButtonGroup>
          }
        >
          <DataColumn>
            <DataCard title='Main'>
              <FormField label='Name' name='name' readOnly={!isEdit} />
              <FormField label='Description' name='description' readOnly={!isEdit} />
              {isEdit ? (
                <FormFieldLayout label='Company' name='tickmillCompany.id'>
                  <Select
                    value={selectedCompanyId}
                    onChange={(nextValue) => {
                      const resolvedValue = Array.isArray(nextValue) ? nextValue[0] : nextValue
                      const companyId = Number(resolvedValue ?? 0)
                      const tickmillCompany = props.tickmillCompanies.find(
                        (company) => company.id === companyId
                      ) ?? {
                        id: 0,
                        name: 'None',
                      }

                      setFieldValue('tickmillCompany', tickmillCompany)
                    }}
                    options={companyOptions}
                    showSearch
                    fullWidth
                    data-test='tickmill-company-field'
                  />
                </FormFieldLayout>
              ) : (
                <FormReadonlyField label='Company' value={selectedCompanyName} />
              )}
              <FormCheckboxField
                name='isCustomSpreadMarkup'
                label='Customer Spread Markup'
                readOnly={!isEdit}
              />
            </DataCard>

            <DataCard title='Standard Commissions'>
              <div className='flex flex-wrap gap-2'>
                <AddButton disabled={!isEdit} text='Add' onClick={handleAddStandardCommissions} />
                <AddButton disabled={!isEdit} text='Upload' onClick={onUploadModalOpen} />
                <AddButton disabled={!isEdit} text='Download' onClick={onDownload} />
              </div>
              <StandardCommissionsGrid
                tableData={props.tableData}
                standardCommissions={values.standardCommissions}
                introducingBrokerProfileAccountType={props.introducingBrokerProfileAccountType}
                isEdit={isEdit}
                onHandleDeleteStandardCommission={handleDeleteStandardCommission}
                onEditStandardCommissions={handleEditStandardCommissions}
                onHandleGridPageChange={props.onHandleGridPageChange}
                onHandleGridSortChange={props.onHandleGridSortChange}
              />
            </DataCard>
          </DataColumn>
        </DataGrid>
      </Form>
    </>
  )
}

interface OuterProps {
  introducingBrokerProfile: IntroducingBrokerDetailedProfileDto
  introducingBrokerProfileAccountType: IntroducingBrokerProfileAccountType[]
  tradingAccountGroupType: AccountGroupDto[]
  tickmillCompanies: TickmillCompanyDto[]
  permissions: PermissionDto[]

  onUploadModalOpen(): void
  onDownload(): void
  onSubmit(values: FormSubmitValues<IntroducingBrokerProfileFormSubmitValues>): Promise<void>
  handleClone(): Promise<void>
  setAuditLogModal(value: boolean): void
  tableData: TableStateContextData
  onSetTableData: Dispatch<SetStateAction<TableStateContextData>>
  onHandleGridPageChange(pageNumber: number): void
  onHandleGridSortChange(event: GridSortChangeEvent): void
}

export type IntroducingBrokerProfileFormSubmitValues = UpdateIntroducingBrokerProfile

const normalizeInstrument = (
  standardCommissions: StandardCommission[]
): StandardCommissionDto[] => {
  return standardCommissions.map((sc) => ({
    ...sc,
    instruments: sc.instruments?.map((instrument) => ({
      id: instrument.id,
      name: instrument.symbol,
    })),
  }))
}

function dataToSubmit(
  values: IntroducingBrokerDetailedProfileDto
): FormSubmitValues<IntroducingBrokerProfileFormSubmitValues> {
  return {
    description: values.description || '',
    id: values.id,
    name: values.name,
    standardCommissions: normalizeInstrument(values.standardCommissions),
    tickmillCompanyId: values?.tickmillCompany?.id ?? null,
    isCustomSpreadMarkup: values.isCustomSpreadMarkup,
  }
}

export const IntroducingBrokerProfileDetailsForm = withFormik<
  OuterProps,
  IntroducingBrokerDetailedProfileDto
>({
  mapPropsToValues: ({ introducingBrokerProfile }) => {
    return {
      ...introducingBrokerProfile,
      standardCommissions: introducingBrokerProfile.standardCommissions,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<IntroducingBrokerProfileFormSubmitValues> = {}
    if (!values.name) {
      errors.name = 'Required'
    }
    return errors
  },
  enableReinitialize: true,
})(IntroducingBrokerProfileFormUI)
